import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Printer, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useMachines, useCreateMachine, useUpdateMachine, useDeleteMachine } from '@/hooks/useTextprint';
import {
  STATUT_MACHINE_LABELS, TYPE_MACHINE_LABELS,
  type MachineImpression, type StatutMachineImpression, type TypeMachine,
} from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutMachineImpression, string> = {
  operationnelle: 'ds-bd ds-bd-s',
  en_maintenance: 'ds-bd ds-bd-w',
  en_panne:       'ds-bd ds-bd-d',
  hors_service:   'ds-bd ds-bd-n',
};

export const MachinesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutMachineImpression | ''>('');
  const [typeMachine, setTypeMachine] = useState<TypeMachine | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<MachineImpression | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useMachines({ statut: statut || undefined, typeMachine: typeMachine || undefined, limit: 200 });
  const machines = data?.data ?? [];
  const remove = useDeleteMachine();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (m: MachineImpression) => { setEditing(m); setDialogOpen(true); };

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer la machine "${code}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const count = (s: StatutMachineImpression) => machines.filter((m) => m.statut === s).length;

  return (
    <>
      <Topbar
        title="Parc machines"
        subtitle={filiale ? `${machines.length} machine(s) · ${filiale.fullName}` : 'Machines'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouvelle machine</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">🖨️</div><div className="ds-kr-val">{machines.length}</div><div className="ds-kr-lbl">Total parc</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">✅</div><div className="ds-kr-val">{count('operationnelle')}</div><div className="ds-kr-lbl">Opérationnelles</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">🔧</div><div className="ds-kr-val">{count('en_maintenance')}</div><div className="ds-kr-lbl">Maintenance</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">🚨</div><div className="ds-kr-val">{count('en_panne')}</div><div className="ds-kr-lbl">Pannes</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutMachineImpression | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_MACHINE_LABELS) as StatutMachineImpression[]).map((s) => <option key={s} value={s}>{STATUT_MACHINE_LABELS[s]}</option>)}
          </select>
          <select className="ds-fi" value={typeMachine} onChange={(e) => setTypeMachine(e.target.value as TypeMachine | '')} style={{ width: 'auto' }}>
            <option value="">Tous types</option>
            {(Object.keys(TYPE_MACHINE_LABELS) as TypeMachine[]).map((t) => <option key={t} value={t}>{TYPE_MACHINE_LABELS[t]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Machines</div><div className="ds-table-sub">{machines.length} machine{machines.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 100 }}>Code</th>
              <th>Nom / Modèle</th>
              <th style={{ width: 140 }}>Type</th>
              <th className="ds-am" style={{ width: 110 }}>Compteur</th>
              <th style={{ width: 130 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && machines.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                  <Printer size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune machine</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Ajouter la première machine</button>
                </td></tr>
              )}
              {!isLoading && machines.map((m) => (
                <tr key={m.id}>
                  <td><span className="ds-ref-code">{m.code}</span></td>
                  <td>
                    <div className="ds-entry-label">{m.nom}</div>
                    {m.modele && <div className="ds-entry-sub">{m.modele}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>{TYPE_MACHINE_LABELS[m.typeMachine]}</td>
                  <td className="ds-am">{m.compteurActuel.toLocaleString('fr-FR')}</td>
                  <td><span className={STATUT_BADGE[m.statut]}>{STATUT_MACHINE_LABELS[m.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(m)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(m.id, m.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <MachineFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} machine={editing} />
    </>
  );
};

const MachineFormDialog = ({ open, onClose, machine }: { open: boolean; onClose: () => void; machine?: MachineImpression }) => {
  const isEdit = !!machine;
  const [code, setCode]           = useState('');
  const [nom, setNom]             = useState('');
  const [typeMachine, setTypeMachine] = useState<TypeMachine>('presse_numerique');
  const [modele, setModele]       = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [dateAcquisition, setDateAcquisition] = useState('');
  const [prixAchat, setPrixAchat] = useState('');
  const [compteur, setCompteur]   = useState('');
  const [statut, setStatut]       = useState<StatutMachineImpression>('operationnelle');
  const [garantieExpire, setGarantieExpire] = useState('');
  const [contratSav, setContratSav] = useState(false);
  const [contactSav, setContactSav] = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  const create = useCreateMachine();
  const update = useUpdateMachine();

  useEffect(() => {
    if (!open) return;
    if (machine) {
      setCode(machine.code); setNom(machine.nom); setTypeMachine(machine.typeMachine);
      setModele(machine.modele ?? ''); setNumeroSerie(machine.numeroSerie ?? '');
      setFournisseur(machine.fournisseur ?? '');
      setDateAcquisition(machine.dateAcquisition?.slice(0, 10) ?? '');
      setPrixAchat(machine.prixAchatCents ? String(Number(BigInt(machine.prixAchatCents)) / 100) : '');
      setCompteur(machine.compteurActuel.toString());
      setStatut(machine.statut);
      setGarantieExpire(machine.garantieExpire?.slice(0, 10) ?? '');
      setContratSav(machine.contratSav); setContactSav(machine.contactSav ?? '');
      setNotes(machine.notes ?? '');
    } else {
      setCode(''); setNom(''); setTypeMachine('presse_numerique'); setModele('');
      setNumeroSerie(''); setFournisseur(''); setDateAcquisition(''); setPrixAchat('');
      setCompteur('0'); setStatut('operationnelle'); setGarantieExpire('');
      setContratSav(false); setContactSav(''); setNotes('');
    }
    setError(null);
  }, [open, machine]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(), nom: nom.trim(), typeMachine,
        modele: modele.trim() || undefined, numeroSerie: numeroSerie.trim() || undefined,
        fournisseur: fournisseur.trim() || undefined,
        dateAcquisition: dateAcquisition || undefined,
        prixAchatCents: prixAchat ? Math.round(parseFloat(prixAchat) * 100) : undefined,
        compteurActuel: compteur ? parseInt(compteur, 10) : 0,
        statut,
        garantieExpire: garantieExpire || undefined,
        contratSav, contactSav: contactSav.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      if (isEdit && machine) await update.mutateAsync({ id: machine.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && nom.trim();

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${machine?.code}` : 'Nouvelle machine'} subtitle="Textprint — parc imprimerie" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Code *"><input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="MAC-001" /></Field>
          <Field label="Type *">
            <select value={typeMachine} onChange={(e) => setTypeMachine(e.target.value as TypeMachine)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TYPE_MACHINE_LABELS) as TypeMachine[]).map((t) => <option key={t} value={t}>{TYPE_MACHINE_LABELS[t]}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutMachineImpression)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_MACHINE_LABELS) as StatutMachineImpression[]).map((s) => <option key={s} value={s}>{STATUT_MACHINE_LABELS[s]}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Nom *"><input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Konica C4080 #1" /></Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Modèle"><input value={modele} onChange={(e) => setModele(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Konica Minolta C4080" /></Field>
          <Field label="N° série"><input value={numeroSerie} onChange={(e) => setNumeroSerie(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Fournisseur"><input value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Date acquisition"><input type="date" value={dateAcquisition} onChange={(e) => setDateAcquisition(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Prix achat (FCFA)"><input type="number" min="0" step="1" value={prixAchat} onChange={(e) => setPrixAchat(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Compteur actuel"><input type="number" min="0" value={compteur} onChange={(e) => setCompteur(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Fin garantie"><input type="date" value={garantieExpire} onChange={(e) => setGarantieExpire(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Contrat SAV">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32 }}>
              <input type="checkbox" checked={contratSav} onChange={(e) => setContratSav(e.target.checked)} />
              <span style={{ fontSize: 12 }}>Oui</span>
            </label>
          </Field>
          <Field label="Contact SAV"><input value={contactSav} onChange={(e) => setContactSav(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer la machine'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
